import { IconButton } from "@components/button";
import {
    ChatHandOverIcon,
    FlagDotFilledGrayIcon,
    FlagDotFilledIcon,
    FlagPlainIcon,
    IProps as IIconProps
} from "@components/icon";
import { ISelectionChangeArgs } from "@components/inputs/select/Select.types";
import UserIconSelect from "@components/inputs/select/UserIconSelect";
import { TCustomRowAction } from "@components/smart/smartTable/SmartTable.utils";
import { IActionRendererArgs, IRow, IRowAction, TId } from "@components/table";
import { IRowProps } from "@components/table/Rows.types";
import { IChatEntity } from "@odata/GeneratedEntityTypes";
import { WebSocketMessageTypeCode } from "@odata/GeneratedEnums";
import HandoverDialog from "@pages/chats/HandoverDialog";
import customFetch, { getDefaultPostParams } from "@utils/customFetch";
import memoizeOne from "@utils/memoizeOne";
import { TWebsocketMessage } from "@utils/websocketManager/Websocket.types";
import i18next from "i18next";
import { debounce } from "lodash";
import { TableStorage } from "model/TableStorage";
import React from "react";

import { TOGGLE_CHATS_FLAGS } from "../../constants";
import { ChatOperationType, ContextEvents, IAppContext } from "../../contexts/appContext/AppContext.types";
import { WithAuthContext, withAuthContext } from "../../contexts/authContext/withAuthContext";
import { ActionState, IconSize, RowAction, Status, ToggleState, ToolbarItemType } from "../../enums";
import { IModifierKeys } from "../../global.types";
import BindingContext from "../../odata/BindingContext";
import WebsocketManager from "../../utils/websocketManager/WebsocketManager";
import TableView from "../../views/table";
import { TableActionOrder, TableButtonsAction, TTableToolbarItem } from "../../views/table/TableToolbar.utils";
import { ITableViewBaseProps } from "../../views/table/TableView";
import { getConfirmationActionText } from "../../views/table/TableView.render.utils";
import { isUserCustomer } from "../admin/users/Users.utils";
import { IChatCustomData, TAB_MINE, TAB_OTHERS } from "./Chats.def";
import {
    canTakeOverChat,
    checkChatMessageNotification, handover,
    hasUnreadMessage,
    IChatOperationArgs,
    isMessageMine,
    myUserProp
} from "./Chats.utils";

interface IProps extends WithAuthContext, ITableViewBaseProps<IChatCustomData> {
}

const FLAG_ACTION_ID = "FLAG_ACTION_ID";
const HANDOVER_ACTION_ID = "HANDOVER_ACTION_ID";

class ChatsTableView extends TableView<IProps> {
    _cancelWebsocketSubscription: () => void;
    _activeRows: Set<string> = new Set();
    _showHandoverDlg = false;
    _toggleHandover = false;

    constructor(props: IProps, context: IAppContext) {
        super(props);

        this._cancelWebsocketSubscription = WebsocketManager.subscribe({
            callback: this.handleTicketNotification,
            types: [WebSocketMessageTypeCode.Notification]
        });

        props.storage.context.eventEmitter.on(ContextEvents.ChatOperation, this.chatOperation);

    }

    get storage() {
        return this.props.storage as TableStorage<IChatEntity, IChatCustomData>;
    }

    get isInDialog() {
        return this.storage.getCustomData().isInDialog;
    }

    get shouldRenderBreadcrumbs(): boolean {
        return !this.isInDialog;
    }

    componentWillUnmount() {
        this.context.eventEmitter.off(ContextEvents.ChatOperation, this.chatOperation);
        this._cancelWebsocketSubscription();
    }

    chatOperation = debounce(async (args?: IChatOperationArgs): Promise<void> => {
        const { storage } = this.props;

        if (args.type === ChatOperationType.Read && args.chatBc) {
            await storage.tableAPI.reloadRow(args.chatBc);
        }
    }, 0);

    handleTableLoad(): void {
        super.handleTableLoad();

        // for opening in document, open with chat right away
        const rows = this.props.storage.tableAPI?.getState().rows;
        if (this.isInDialog && Object.keys(rows).length === 1) {
            this.handleRowSelect(Object.values(rows)[0].id as BindingContext);
        }
    }

    handleRowSelect = (bindingContext: BindingContext, props?: IRowProps, modifiers?: IModifierKeys): void => {
        this.props.onRowSelect?.(bindingContext, null, null, this.props.storage.getCustomData());
    };


    handleTicketNotification = async (message: TWebsocketMessage): Promise<void> => {
        const { storage } = this.props;
        const data = await checkChatMessageNotification(message, storage.oData);

        if (data.ChatId) {
            if (data.isChatMessage) {
                const openedRowBc = storage.data.rowBindingContext;
                const bc = storage.data.bindingContext.addKey(data.ChatId);
                const shouldSkip = openedRowBc?.getKey() === data.ChatId;

                if (!shouldSkip) {
                    storage.tableAPI.reloadRow(bc);
                }
            }

            if (data.isChat) {
                storage.tableAPI.reloadTable();
            }
        }
    };

    handleNewItemAddFromUserSelect = (args: ISelectionChangeArgs) => {
        const storage = this.props.storage;
        const cd = storage.getCustomData();

        this.props.onAdd({
            customData: {
                user: {
                    FirstName: args.additionalData?.FirstName,
                    LastName: args.additionalData?.LastName,
                    id: args.value
                },
                isInDialog: this.isInDialog,
                isDraft: cd.isDraft,
                id: cd.id,
                entityType: cd.entityType,
                numberOurs: cd.numberOurs,
                documentBindingContext: cd.documentBindingContext
            }
        });
    }

    getFlagIcon = (): TTableToolbarItem => {
        const tabId = this.state.selectedSecondaryFilter;
        const isMine = tabId === TAB_MINE || !tabId /* mine tab or not using tabs when customer*/;
        return {
            id: "flag",
            itemType: ToolbarItemType.Custom,
            order: TableActionOrder.Add + 2,
            render: () => {
                const action = this.getTableAction();
                const isAction = action && action !== FLAG_ACTION_ID;
                const isAddingNew = this.isAddingRow;

                return (
                    <IconButton title={i18next.t("Chat:Flag")} isTransparent
                                key={"FlagIconButton"}
                                hotspotId="FlagChat"
                                isDisabled={!isMine || isAction || isAddingNew}
                                onClick={this.handleFlagClick}
                                isActive={action === FLAG_ACTION_ID}>
                        <FlagPlainIcon width={IconSize.S}/>
                    </IconButton>
                );
            }
        };
    }

    getHandoverIcon = (): TTableToolbarItem => {
        return {
            id: "handover",
            itemType: ToolbarItemType.Custom,
            order: TableActionOrder.Add + 1,
            render: () => {
                const action = this.getTableAction();
                const isAction = action && action !== HANDOVER_ACTION_ID;
                const isAddingNew = this.isAddingRow;

                return (
                    <IconButton title={this.getCorrectHandoverLbl()} isTransparent
                                key={"HandoverButton"}
                                hotspotId="ChatHandover"
                                isDisabled={isAction || isAddingNew}
                                onClick={this.handleHandoverClick}
                                isActive={action === HANDOVER_ACTION_ID}>
                        <ChatHandOverIcon width={IconSize.S}/>
                    </IconButton>
                );
            }
        };
    }

    getInitialActiveRows = memoizeOne((): BindingContext[] => {
        const activeRows = this.props.storage.tableAPI?.getRowsArray().filter(row =>
            this.isRowFlagged(row));

        return activeRows?.map(row => this.props.storage.data.bindingContext.addKey(row.dataId));
    }, () => [this.props.storage.tableAPI?.getState()?.loaded]);


    getTableSharedProps = () => {
        const props = super.getTableSharedProps();

        props.keepActiveRowsOnFilterChange = true;
        props.considerInitialActiveRowsAsChanged = true;
        props.initialActiveRows = this.getInitialActiveRows();

        return props;
    };

    handleHandoverClick = () => {
        this.handleCustomAction(HANDOVER_ACTION_ID);
    }

    handleFlagClick = (): void => {
        this.handleCustomAction(FLAG_ACTION_ID);
    };

    hasFlagsChanged = () => {
        const _isEqual = (xs: Set<string>, rows: IRow[]) =>
            xs.size === rows?.length && !rows.some((row: IRow) => !xs.has(row.id.toString()));

        const activeRows = this.props.storage.tableAPI?.getState()?.activeRows;
        const oldActiveRows = this.props.storage.tableAPI?.getRowsArray().filter(row =>
            this.isRowFlagged(row));

        return !_isEqual(activeRows, oldActiveRows);
    }

    beforeRefresh = async (): Promise<void> => {
        this.getInitialActiveRows.reset();
        await this.updateSecondaryQueryValues();
    };

    async handleToolbarConfirm(): Promise<void> {
        const tableAction = this.getTableAction();

        if (tableAction === HANDOVER_ACTION_ID) {
            const isMine = this.state.selectedSecondaryFilter === TAB_MINE;
            if (isMine) {
                this._showHandoverDlg = true;
                this.forceUpdate();
            } else {
                const me = this.props.storage.context.getData().userSettings;
                const error = await handover(Array.from(this._activeRows), me?.Id.toString());
                if(!error) {
                    this.handleHandoverFinish(true);
                } else {
                    this.props.storage.setTableAlert(error);
                }
            }
        } else if (tableAction === FLAG_ACTION_ID) {
            const tableAPI = this.props.storage.tableAPI;
            const activeRowsSet = tableAPI?.getState()?.activeRows;
            const allRows = tableAPI?.getRowsArray();
            const oldActiveRows = tableAPI?.getRowsArray().filter(row => this.isRowFlagged(row));

            const rows: string[] = [];
            for (const rowKey of [...activeRowsSet]) {
                const row = allRows.find(row => row.id.toString() === rowKey);
                if (!this.isRowFlagged(row)) {
                    rows.push(row.dataId);
                }
            }

            for (const row of oldActiveRows) {
                if (!activeRowsSet.has(row.id.toString())) {
                    rows.push(row.dataId);
                }
            }

            const body = {
                ChatIds: rows
            };

            await customFetch(TOGGLE_CHATS_FLAGS, {
                ...getDefaultPostParams(),
                body: JSON.stringify(body)
            });
            this.getInitialActiveRows.reset();
            await this.setTableAction(null);
            this.props.storage.tableAPI.reloadTable();
        } else {
            await super.handleToolbarConfirm();
        }
    }

    isRowFlagged = (row: IRowProps) => {
        const myProp = myUserProp(this.context, row?.customData?.entity);
        return row?.customData?.entity?.[myProp]?.IsFlagged;
    }

    isRowWithoutAction(rowId: TId, action: RowAction, row: IRow): boolean {
        const tableAction = this.getTableAction();
        if (tableAction === HANDOVER_ACTION_ID) {
            return !canTakeOverChat(row?.customData?.entity, this.props.storage.context);
        }

        if (tableAction === FLAG_ACTION_ID) {
            return !isMessageMine(this.props.storage.context.getData().userSettings, row?.customData?.entity);
        }

        return false;
    }

    getRowIcon = (id: TId, row: IRowProps, rowAction: IRowAction): React.ComponentType<IIconProps> => {
        if (rowAction) {
            return null;
        }

        return this.isRowFlagged(row) ? FlagDotFilledGrayIcon : null;
    };

    isTabsDisabled = () => {
        const action = this.getTableAction();
        return action === FLAG_ACTION_ID || action === HANDOVER_ACTION_ID;
    }

    allHandoverRows = () => {
        const rows = this.props.storage.tableAPI?.getState().rows;
        return Object.values(rows)?.filter(row => !this.isRowWithoutAction(row.id, null, row));
    }

    getCustomRowAction = memoizeOne((): TCustomRowAction => {
        const tableAction = this.getTableAction();
        if (tableAction === HANDOVER_ACTION_ID) {
            return {
                actionType: RowAction.Custom,
                getActionState: (rowId: TId, row: IRowProps) => {
                    return this._activeRows.has(row?.dataId) ? ActionState.Active : ActionState.Inactive;
                },
                onClick: (rowId: TId, row: IRowProps) => {
                    this._activeRows.has(row?.dataId) ? this._activeRows.delete(row?.dataId) : this._activeRows.add(row?.dataId);
                    const allRows = this.allHandoverRows();
                    this._toggleHandover = this._activeRows.size === allRows.length;
                },
                onToggleChange: (toggleState: ToggleState) => {
                    this._toggleHandover = !this._toggleHandover;

                    if (!this._toggleHandover) {
                        this._activeRows.clear();
                    } else {
                        const rows = this.props.storage.tableAPI?.getState().rows;
                        for (const row of Object.values(rows)) {
                            if (!this.isRowWithoutAction(row.id, null, row)) {
                                this._activeRows.add(row?.dataId);
                            }
                        }
                    }
                    this.props.onActiveRowActionCountChange?.(this._activeRows.size);
                    // refresh rows' checkboxes
                    this.props.storage.tableAPI?.forceUpdate();
                    // refresh confirm button
                    this.forceUpdate();
                },
                onTableReloaded: null,
                toggleState: () => {
                    return this._toggleHandover ? ToggleState.AllChecked : ToggleState.AllUnchecked;
                }
            };
        }

        if (tableAction === FLAG_ACTION_ID) {
            return {
                actionType: RowAction.Custom,
                isSingleSelect: false,
                render: (args: IActionRendererArgs) => {
                    const Icon = args.actionState === ActionState.Active ? FlagDotFilledIcon : FlagPlainIcon;
                    return (
                        <IconButton title=""
                                    key={"FlagIcon"}
                                    onClick={args.onClick}
                                    isDisabled={args.actionState === ActionState.None || args.isDisabled || args.isLocked}
                                    isDecorative>
                            <Icon width={IconSize.S} height={IconSize.S}/>
                        </IconButton>
                    );
                }
            };
        }

        return null;
    }, () => [this.getTableAction(), this._toggleHandover]);

    handleTabChange = (id: string) => {
        if (id === TAB_MINE) {
            this.getInitialActiveRows.reset();
        }

        this._toggleHandover = false;
        this._activeRows.clear();

        return super.handleTabChange(id);
    }


    getAddSelectDef = (): TTableToolbarItem => {
        const myId = this.props.storage?.context?.getData().userSettings?.Id;
        const tableAction = this.getTableAction();
        const isAddingNew = this.isAddingRow;

        return {
            id: "addSelect",
            itemType: ToolbarItemType.Custom,
            order: TableActionOrder.Add,
            render: () => {
                return (
                    <UserIconSelect
                        isDisabled={!!tableAction}
                        skippedUserId={myId?.toString()}
                        iconName="Add"
                        isCustomer={isUserCustomer(this.props.storage.context.getData().userSettings)}
                        isActive={isAddingNew}
                        onChange={this.handleNewItemAddFromUserSelect}
                    />
                );
            }
        };
    };

    getToolbarButtons(): TableButtonsAction[] {
        return [
            TableButtonsAction.Settings,
            TableButtonsAction.Sorting
        ];
    }

    canConfirmToolbarAction = (): boolean => {
        const tableAction = this.getTableAction();
        if (tableAction === FLAG_ACTION_ID) {
            return this.hasFlagsChanged();
        }

        if (tableAction === HANDOVER_ACTION_ID) {
            return this._activeRows.size > 0;
        }

        return super.canConfirmToolbarAction();
    };

    get customToolbarItems(): TTableToolbarItem[] {
        const isCustomer = isUserCustomer(this.props.storage.context.getData().userSettings);

        return [
            this.getAddSelectDef(),
            this.getFlagIcon(),
            ...(!isCustomer ? [this.getHandoverIcon()] : []),
        ];
    }

    rowsFactory(rows: IRow[]): IRow[] {
        //filter flagged messages that do not belong the the selected tab
        const tab = this.state.selectedSecondaryFilter;
        const isTabMine = tab === TAB_MINE || !tab /*tables with no tabs (customers) shows every mine message */;
        const isTabOthers = tab === TAB_OTHERS;

        return rows.filter((row: IRow) => {
            const isMine = isMessageMine(this.props.storage.context.getData().userSettings, row?.customData?.entity);
            return (isMine && isTabMine) || (!isMine && isTabOthers);
        }).map(row => {
            if (!row) {
                return undefined;
            }

            const hasUnreadMessages = hasUnreadMessage(this.props.storage, row.customData.entity);
            row.statusHighlight = hasUnreadMessages ? Status.Warning : null;
            row.isBold = hasUnreadMessages;
            return row;
        });
    }

    getCorrectHandoverLbl = () => {
        const tabId = this.state.selectedSecondaryFilter;
        return tabId === TAB_MINE ? this.props.storage.t("Chat:Handover") : this.props.storage.t("Chat:Takeover");
    }

    getConfirmText(tableAction: TableButtonsAction | string): string | React.ReactElement {
        if (tableAction === HANDOVER_ACTION_ID) {
            return getConfirmationActionText(this.getCorrectHandoverLbl(), this._activeRows.size);
        }
        if (tableAction === FLAG_ACTION_ID) {
            const activeRowActionCount = this.props.storage.tableAPI?.getState().activeRows?.size;
            return getConfirmationActionText(this.props.storage.t("Chat:Flag"), activeRowActionCount);
        }

        return super.getConfirmText(tableAction);
    }

    handleHandoverFinish = async (reload: boolean) => {
        this._activeRows.clear();
        this._showHandoverDlg = false;
        await this.setTableAction(null);

        if (reload) {
            // clear active rows for proper table load otherwise it will load handovered items too
            this.props.storage.tableAPI.setState({
                activeRows: new Set<string>()
            });
            await this.props.storage.tableAPI.reloadTable();
        }

        this.forceUpdate();
    }

    renderHandoverDlg = () => {
        if (this._showHandoverDlg) {
            const currentUser = this.props.storage.context.getData().userSettings;
            const isMine = this.state.selectedSecondaryFilter === TAB_MINE;
            return (
                <HandoverDialog
                    skippedUserId={isMine ? currentUser?.Id?.toString() : null}
                    chatIds={Array.from(this._activeRows)}
                    onClose={this.handleHandoverFinish}/>
            );
        }

        return null;
    }

    render() {
        return (
            <>
                {this.renderHandoverDlg()}
                {super.render()}
            </>
        );
    }
}

export default withAuthContext(ChatsTableView);